import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default from 'react';
import linkStyles from './Link.styles.esm.js';
import { LinkAnchor } from './LinkAnchor.esm.js';
import { LinkButton } from './LinkButton.esm.js';

/**
 * Link component with intelligent rendering based on props.
 *
 * **Three Rendering Modes:**
 *
 * 1. **Anchor Mode** (when `href` is provided):
 *    - Renders as `<a>` element for traditional navigation
 *    - Use for external links, internal page navigation, and downloadable resources
 *    - Example: `<Link href="/about">About Us</Link>`
 *
 * 2. **Next.js Integration** (when `asChild` is true):
 *    - Renders as `<span>` wrapper for Next.js Link composition
 *    - Use with Next.js Link component for client-side routing
 *    - Example: `<NextLink href="/dashboard"><Link asChild>Dashboard</Link></NextLink>`
 *
 * 3. **Button Mode** (when neither `href` nor `asChild` are provided):
 *    - Renders as `<button>` element for JavaScript-based interactions
 *    - Use for SPA navigation, modal triggers, form actions, or analytics tracking
 *    - Maintains visual consistency with link styles while providing button semantics
 *    - Example: `<Link onClick={() => navigate('/dashboard')}>Dashboard</Link>`
 *
 * **Button Mode Use Cases:**
 * - React Router navigation: `onClick={() => navigate('/path')}`
 * - Analytics tracking: `onClick={() => trackEvent('link_clicked')}`
 * - Form validation: `onClick={() => validateAndSubmit()}`
 * - Modal/dialog triggers: `onClick={() => setIsOpen(true)}`
 *
 * @param href - URL for anchor mode navigation
 * @param asChild - Enable Next.js Link composition mode
 * @param onClick - Click handler (typically used in button mode)
 * @param variant - Visual style: 'inline' or 'standalone'
 * @param size - Text size: 'xxs' | 'xs' | 'sm' | 'md'
 */
const Link = React__default.forwardRef((_a, ref) => {
    var { className, children, icon, inverse = false, size = "md", weight = "regular", iconPosition = "left", iconSize = "md", variant = "inline", asChild, href } = _a, props = __rest(_a, ["className", "children", "icon", "inverse", "size", "weight", "iconPosition", "iconSize", "variant", "asChild", "href"]);
    const { base: baseClasses, baseFont: baseFontClasses, icon: iconClasses, } = linkStyles({
        variant,
        size,
        iconPosition,
        iconSize,
        weight,
        inverse,
    });
    const content = (jsxs(Fragment, { children: [React__default.isValidElement(icon) &&
                iconPosition &&
                React__default.cloneElement(icon, {
                    className: `${iconClasses()} ${icon.props.className || ""}`,
                }), children] }));
    // NextLink case
    if (asChild) {
        return (jsx("span", Object.assign({ ref: ref, className: `${baseClasses({ class: className })} ${baseFontClasses()}`, tabIndex: 0 }, props, { children: content })));
    }
    if (href) {
        return (jsx(LinkAnchor, Object.assign({ ref: ref, className: `${baseClasses({ class: className })} ${baseFontClasses()}`, tabIndex: 0, href: href }, props, { children: content })));
    }
    return (jsx(LinkButton, Object.assign({ ref: ref, className: `${baseClasses({ class: className })} ${baseFontClasses()}`, tabIndex: 0 }, props, { children: content })));
});
// Add display name for better debugging
Link.displayName = "Link";

export { Link };
